/**
 * The upload queue behind the Photos and Documents toolbar.
 *
 * One hook so the page itself stays presentational. Per the spec the flow for
 * every file is: POST .../files in batches of up to 50 to get presigned PUT
 * URLs, PUT the bytes straight to S3 with the file's own Content-Type (XHR, so
 * progress is reported), then POST .../files/{id}/complete to flip the row
 * from pending to ready. At most three PUTs are in flight at a time, and the
 * folder listing is invalidated as files land so the grid fills in behind the
 * progress panel.
 *
 * Presigned PUTs last ten minutes, so a batch is presigned and uploaded before
 * the next batch is presigned rather than signing everything up front. A file
 * that fails for any reason (expired URL, network, S3) stays in the panel with
 * its reason and a Retry button that re-presigns it from scratch.
 *
 * Folder upload (<input webkitdirectory>) first recreates the tree: the
 * distinct directory paths are created top-down with POST .../folders, and a
 * `conflict` (the name already exists in that parent) reuses the existing
 * folder instead of failing, so uploading into an existing tree merges.
 */

import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  UPLOAD_BATCH_SIZE,
  completeUpload,
  createStoreFolder,
  fetchStoreFolderListing,
  isFolderConflict,
  putWithProgress,
  requestUploadSlots,
  storeFolderQueryKey,
  type UploadSlotRequest,
} from '@/lib/useStoreFiles';
import { chunk, planFolderUpload, splitFolderPath } from './storeFilesUtils';

/** At most three byte streams at once, per the spec. */
const MAX_IN_FLIGHT = 3;

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export type UploadItem = {
  uid: string;
  /** The file name as it will be listed. */
  name: string;
  /** The folder path a directory upload put it in, '' for a plain upload. */
  path: string;
  size: number;
  status: UploadStatus;
  /** 0 to 1. */
  progress: number;
  error: string | null;
};

type Pending = { file: File; folderId: number | null };

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

let uidCounter = 0;
function nextUid(): string {
  uidCounter += 1;
  return `u${uidCounter}`;
}

/**
 * Creates (or finds) one folder path under `baseFolderId`, caching the id of
 * every path it resolves so a deeper path can look its parent up.
 */
async function ensureFolderPath(
  storeNo: string,
  path: string,
  baseFolderId: number | null,
  cache: Map<string, number | null>,
): Promise<number> {
  const { parent, name } = splitFolderPath(path);
  const parentId = parent ? (cache.get(parent) ?? null) : baseFolderId;
  try {
    const created = await createStoreFolder(storeNo, { name, parent_id: parentId });
    cache.set(path, created.id);
    return created.id;
  } catch (err) {
    if (!isFolderConflict(err)) throw err;
    // The folder is already there: reuse it rather than failing the upload.
    const listing = await fetchStoreFolderListing(storeNo, parentId);
    const existing = listing.folders.find((f) => f.name.toLowerCase() === name.toLowerCase());
    if (!existing) throw err;
    cache.set(path, existing.id);
    return existing.id;
  }
}

export type UploadQueue = {
  items: UploadItem[];
  /** True while anything is still queued or uploading. */
  busy: boolean;
  /** Files picked with <input multiple>: all land in the current folder. */
  addFiles: (files: File[], folderId: number | null) => void;
  /** Files picked with <input webkitdirectory>: the tree is recreated first. */
  addFolder: (files: File[], folderId: number | null) => void;
  retry: (uid: string) => void;
  /** Drops the finished rows, keeping anything still running or failed. */
  clearFinished: () => void;
};

export function useStoreFileUploads(storeNo: string | null): UploadQueue {
  const qc = useQueryClient();
  const [items, setItems] = useState<UploadItem[]>([]);
  // The File handles and their resolved target folder. Kept in a ref: they are
  // not render data, and a retry needs them long after the first attempt.
  const pending = useRef<Map<string, Pending>>(new Map());

  const patch = useCallback((uid: string, change: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...change } : it)));
  }, []);

  // Uploads one file end to end. The slot is presigned by the caller so a
  // whole batch is signed in one POST.
  const runOne = useCallback(
    async (uid: string, slot: { file_id: number; upload_url: string }) => {
      const entry = pending.current.get(uid);
      if (!entry || !storeNo) return;
      patch(uid, { status: 'uploading', progress: 0, error: null });
      let shown = 0;
      await putWithProgress(slot.upload_url, entry.file, (fraction) => {
        // Only repaint on a whole-percent step: a big file fires hundreds of
        // progress events and each one would re-render the panel.
        const pct = Math.floor(fraction * 100);
        if (pct > shown) {
          shown = pct;
          patch(uid, { progress: fraction });
        }
      });
      await completeUpload(storeNo, slot.file_id);
      patch(uid, { status: 'done', progress: 1 });
      void qc.invalidateQueries({ queryKey: storeFolderQueryKey(storeNo, entry.folderId) });
    },
    [patch, qc, storeNo],
  );

  /**
   * Presigns a batch, then drains it three PUTs at a time. Each file's failure
   * is its own: one bad file does not stop the batch.
   */
  const runBatch = useCallback(
    async (uids: string[]) => {
      if (!storeNo || uids.length === 0) return;
      const slotRequests: UploadSlotRequest[] = [];
      const ordered: string[] = [];
      for (const uid of uids) {
        const entry = pending.current.get(uid);
        if (!entry) continue;
        ordered.push(uid);
        slotRequests.push({
          name: entry.file.name,
          content_type: entry.file.type || 'application/octet-stream',
          size_bytes: entry.file.size,
          folder_id: entry.folderId,
        });
      }
      if (ordered.length === 0) return;

      let slots;
      try {
        slots = await requestUploadSlots(storeNo, slotRequests);
      } catch (err) {
        const reason = messageOf(err);
        for (const uid of ordered) patch(uid, { status: 'error', error: reason });
        return;
      }

      let cursor = 0;
      const worker = async () => {
        for (;;) {
          const index = cursor;
          cursor += 1;
          const uid = ordered[index];
          if (uid === undefined) return;
          const slot = slots[index];
          if (!slot) {
            patch(uid, { status: 'error', error: 'The server did not return an upload slot.' });
            continue;
          }
          try {
            await runOne(uid, slot);
          } catch (err) {
            patch(uid, { status: 'error', error: messageOf(err) });
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(MAX_IN_FLIGHT, ordered.length) }, () => worker()),
      );
    },
    [patch, runOne, storeNo],
  );

  const runAll = useCallback(
    async (uids: string[]) => {
      for (const batch of chunk(uids, UPLOAD_BATCH_SIZE)) {
        await runBatch(batch);
      }
    },
    [runBatch],
  );

  const enqueue = useCallback(
    (entries: Array<{ file: File; folderId: number | null; path: string }>) => {
      const rows: UploadItem[] = [];
      const uids: string[] = [];
      for (const entry of entries) {
        const uid = nextUid();
        pending.current.set(uid, { file: entry.file, folderId: entry.folderId });
        uids.push(uid);
        rows.push({
          uid,
          name: entry.file.name,
          path: entry.path,
          size: entry.file.size,
          status: 'queued',
          progress: 0,
          error: null,
        });
      }
      setItems((prev) => [...prev, ...rows]);
      void runAll(uids);
    },
    [runAll],
  );

  const addFiles = useCallback(
    (files: File[], folderId: number | null) => {
      enqueue(files.map((file) => ({ file, folderId, path: '' })));
    },
    [enqueue],
  );

  const addFolder = useCallback(
    (files: File[], folderId: number | null) => {
      if (!storeNo || files.length === 0) return;
      // The rows appear immediately, queued, while the folders are created, so
      // a big directory does not look like nothing happened.
      const relPaths = files.map((f) => f.webkitRelativePath || f.name);
      const rows: UploadItem[] = [];
      const uids: string[] = [];
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const relPath = relPaths[i];
        if (!file || relPath === undefined) continue;
        const uid = nextUid();
        pending.current.set(uid, { file, folderId });
        uids.push(uid);
        rows.push({
          uid,
          name: file.name,
          path: relPath.split('/').slice(0, -1).join('/'),
          size: file.size,
          status: 'queued',
          progress: 0,
          error: null,
        });
      }
      setItems((prev) => [...prev, ...rows]);

      void (async () => {
        const cache = new Map<string, number | null>();
        try {
          for (const path of planFolderUpload(relPaths)) {
            await ensureFolderPath(storeNo, path, folderId, cache);
          }
        } catch (err) {
          const reason = `Could not create the folder: ${messageOf(err)}`;
          for (const uid of uids) patch(uid, { status: 'error', error: reason });
          return;
        }
        // Point every file at the folder its own path resolved to.
        for (let i = 0; i < uids.length; i += 1) {
          const uid = uids[i];
          const relPath = relPaths[i];
          if (uid === undefined || relPath === undefined) continue;
          const entry = pending.current.get(uid);
          if (!entry) continue;
          const dir = relPath.split('/').slice(0, -1).join('/');
          pending.current.set(uid, { ...entry, folderId: dir ? (cache.get(dir) ?? folderId) : folderId });
        }
        // The new folders are visible in the listing even before the bytes land.
        void qc.invalidateQueries({ queryKey: storeFolderQueryKey(storeNo, folderId) });
        await runAll(uids);
      })();
    },
    [patch, qc, runAll, storeNo],
  );

  const retry = useCallback(
    (uid: string) => {
      if (!pending.current.has(uid)) return;
      patch(uid, { status: 'queued', progress: 0, error: null });
      void runAll([uid]);
    },
    [patch, runAll],
  );

  const clearFinished = useCallback(() => {
    setItems((prev) => {
      for (const it of prev) if (it.status === 'done') pending.current.delete(it.uid);
      return prev.filter((it) => it.status !== 'done');
    });
  }, []);

  return {
    items,
    busy: items.some((it) => it.status === 'queued' || it.status === 'uploading'),
    addFiles,
    addFolder,
    retry,
    clearFinished,
  };
}
